// Ticket Collection Service
import { createProjectCollectionService, orderBy, where, limit } from '../firebase/firestore';
import type { Ticket, TicketStatus, TicketType, TicketPriority } from '@/types';

const ticketService = createProjectCollectionService<Ticket>('tickets');

export const ticketsService = {
  ...ticketService,

  // Get tickets by status
  getByStatus: async (orgId: string, projectId: string, status: TicketStatus) => {
    return ticketService.getByProject(orgId, projectId, [
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get open tickets (new + open + pending)
  getOpenTickets: async (orgId: string, projectId: string) => {
    return ticketService.getByProject(orgId, projectId, [
      where('status', 'in', ['new', 'open', 'pending']),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get tickets assigned to agent
  getAssignedTo: async (orgId: string, projectId: string, agentId: string) => {
    return ticketService.getByProject(orgId, projectId, [
      where('assigneeId', '==', agentId),
      where('status', 'in', ['new', 'open', 'pending']),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get unassigned tickets
  getUnassigned: async (orgId: string, projectId: string) => {
    return ticketService.getByProject(orgId, projectId, [
      where('assigneeId', '==', null),
      where('status', '==', 'new'),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get tickets by type
  getByType: async (orgId: string, projectId: string, type: TicketType) => {
    return ticketService.getByProject(orgId, projectId, [
      where('type', '==', type),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get tickets by priority
  getByPriority: async (orgId: string, projectId: string, priority: TicketPriority) => {
    return ticketService.getByProject(orgId, projectId, [
      where('priority', '==', priority),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Get recent tickets
  getRecent: async (orgId: string, projectId: string, count = 10) => {
    return ticketService.getByProject(orgId, projectId, [
      orderBy('createdAt', 'desc'),
      limit(count),
    ]);
  },

  // Get tickets by submitter email
  getBySubmitter: async (orgId: string, projectId: string, email: string) => {
    return ticketService.getByProject(orgId, projectId, [
      where('submitterEmail', '==', email),
      orderBy('createdAt', 'desc'),
    ]);
  },

  // Assign ticket to agent
  assign: async (ticketId: string, agentId: string, agentName: string) => {
    return ticketService.update(ticketId, {
      assigneeId: agentId,
      assigneeName: agentName,
      status: 'open',
    });
  },

  // Update ticket status
  updateStatus: async (ticketId: string, status: TicketStatus) => {
    return ticketService.update(ticketId, { status });
  },

  // Update ticket priority
  updatePriority: async (ticketId: string, priority: TicketPriority) => {
    return ticketService.update(ticketId, { priority });
  },

  // Add internal note
  addNote: async (_ticketId: string, _note: string) => {
    // Notes would be stored as a subcollection or in the ticket document
    // For simplicity, we'll update the ticket with notes array
    // In production, this would be a subcollection for better scalability
  },

  // Close ticket with resolution
  close: async (ticketId: string, resolution?: string) => {
    return ticketService.update(ticketId, {
      status: 'closed',
      resolution,
      closedAt: new Date() as any,
    });
  },

  // Reopen ticket
  reopen: async (ticketId: string) => {
    return ticketService.update(ticketId, {
      status: 'open',
      closedAt: undefined,
    });
  },
};
